import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const hadToken = !!localStorage.getItem('auth_token')
      const storedUser = localStorage.getItem('auth_user')
      const role = storedUser ? JSON.parse(storedUser).role : null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      // Only force a hard reload into /login if there actually was a
      // session that just expired/got rejected. A 401 with no token to
      // begin with just means the caller isn't logged in yet (e.g. a
      // background fetch that fires on every page, including /login
      // itself, before any session exists) -- reloading in that case
      // produced a visible reload loop on every unauthenticated page view.
      if (hadToken && !window.location.pathname.startsWith('/login')) {
        window.location.href = role === 'admin' ? '/login/admin' : '/login/seller'
      }
    }
    return Promise.reject(error)
  }
)

export default api

export const customersApi = {
  list: () => api.get('/customers'),
  get: (id: number) => api.get(`/customers/${id}`),
  create: (data: any) => api.post('/customers', data),
  update: (id: number, data: any) => api.put(`/customers/${id}`, data),
  delete: (id: number) => api.delete(`/customers/${id}`),
  inactive: () => api.get('/customers/inactive'),
}

export const salesApi = {
  list: () => api.get('/sales'),
  create: (data: any) => api.post('/sales', data),
  createBulk: (data: {
    customer_id: number | null
    items: any[]
    gps_latitude?: number | null
    gps_longitude?: number | null
  }) => api.post('/sales/bulk', data),
  get: (id: number) => api.get(`/sales/${id}`),
  update: (id: number, data: any) => api.put(`/sales/${id}`, data),
  delete: (id: number) => api.delete(`/sales/${id}`),
}

export const routesApi = {
  list: () => api.get('/routes'),
  create: (data: any) => api.post('/routes', data),
  assign: (id: number, data: any) => api.put(`/routes/${id}/assign`, data),
  checkin: (id: number, data: any) => api.post(`/routes/${id}/checkin`, data),
}

export const complaintsApi = {
  list: () => api.get('/complaints'),
  create: (data: any) => api.post('/complaints', data),
  update: (id: number, data: any) => api.put(`/complaints/${id}`, data),
  resolve: (id: number, data: any) => api.put(`/complaints/${id}/resolve`, data),
}

export const sellersApi = {
  list: () => api.get('/sellers'),
  create: (data: any) => api.post('/sellers', data),
  update: (id: number, data: any) => api.put(`/sellers/${id}`, data),
  updateStatus: (id: number, data: any) => api.put(`/sellers/${id}/status`, data),
  updatePermissions: (id: number, data: { edit_customers?: boolean; edit_sales?: boolean }) =>
    api.put(`/sellers/${id}/permissions`, data),
}

export const collaboratorsApi = {
  list: () => api.get('/collaborators'),
  create: (data: { email: string; full_name: string; dni?: string; phone?: string; residency?: string; panels: string[] }) =>
    api.post('/collaborators', data),
  updatePanels: (id: number, panels: string[]) => api.put(`/collaborators/${id}/panels`, { panels }),
}

export const settingsApi = {
  getDashboardPrefs: () => api.get('/settings/dashboard-prefs'),
  updateDashboardPrefs: (prefs: Record<string, boolean>) => api.put('/settings/dashboard-prefs', prefs),
}

export const adminUtilsApi = {
  testEmail: (recipientEmail?: string) => api.post('/admin/test-email', { recipient_email: recipientEmail }),
}

export const trackingApi = {
  updateLocation: (data: any) => api.post('/tracking/location', data),
  getSellerLocations: (id: number) => api.get(`/tracking/seller/${id}`),
  getSellerLatest: (id: number) => api.get(`/tracking/seller/${id}/latest`),
}

export const reportsApi = {
  getSalesReport: (sellerId?: number) =>
    api.get('/reports/sales', { params: sellerId ? { seller_id: sellerId } : {} }),
  getCollections: (sellerId?: number) =>
    api.get('/reports/collections', { params: sellerId ? { seller_id: sellerId } : {} }),
  getMargin: (sellerId?: number) =>
    api.get('/reports/margin', { params: sellerId ? { seller_id: sellerId } : {} }),
  getByDistrict: (startDate?: string, endDate?: string) =>
    api.get('/reports/by-district', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  getByBusinessType: (startDate?: string, endDate?: string) =>
    api.get('/reports/by-business-type', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  getCashPosition: (startDate?: string, endDate?: string) =>
    api.get('/reports/cash-position', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  getCashFlowTrend: (startDate?: string, endDate?: string) =>
    api.get('/reports/cash-flow-trend', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  getCustomersTrend: () => api.get('/reports/customers-trend'),
  getOrdersTrend: () => api.get('/reports/orders-trend'),
  getInventoryRotation: (startDate?: string, endDate?: string, agingThresholdDays?: number) =>
    api.get('/reports/inventory-rotation', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(agingThresholdDays ? { aging_threshold_days: agingThresholdDays } : {}),
      },
    }),
}

export const customerTrackingApi = {
  getTracking: () => api.get('/tracking/customers'),
}

export const authApi = {
  register: (data: { email: string; password: string; full_name: string; role: string }) =>
    api.post('/auth/register', data),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, new_password: string) =>
    api.post('/auth/reset-password', { token, new_password }),
}

export const profileApi = {
  get: () => api.get('/profile'),
  update: (data: any) => api.put('/profile', data),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.put('/profile/password', data),
}

export const productsApi = {
  list: () => api.get('/products'),
  create: (data: any) => api.post('/products', data),
  update: (id: number, data: any) => api.put(`/products/${id}`, data),
  delete: (id: number) => api.delete(`/products/${id}`),
}

export const sellerProductsApi = {
  list: () => api.get('/seller/products'),
  add: (data: any) => api.post('/seller/products', data),
  update: (id: number, data: any) => api.put(`/seller/products/${id}`, data),
  remove: (id: number) => api.delete(`/seller/products/${id}`),
}

export const customerProductsApi = {
  list: (customerId: number) => api.get(`/customers/${customerId}/products`),
  add: (customerId: number, data: any) => api.post(`/customers/${customerId}/products`, data),
  update: (id: number, data: any) => api.put(`/customer-products/${id}`, data),
  remove: (id: number) => api.delete(`/customer-products/${id}`),
}

// FX Admin -- a second, unrelated small business (simple buy-sell trading)
// managed from the same portal, under its own Postgres schema
// (distribution_fx). See infra/db/027_add_distribution_fx.sql.
export const fxSuppliersApi = {
  list: () => api.get('/fx/suppliers'),
  create: (data: any) => api.post('/fx/suppliers', data),
  update: (id: number, data: any) => api.put(`/fx/suppliers/${id}`, data),
}

export const fxProductsApi = {
  list: () => api.get('/fx/products'),
  create: (data: any) => api.post('/fx/products', data),
  update: (id: number, data: any) => api.put(`/fx/products/${id}`, data),
}

// Official Fuxion price list (precios_fuxion.csv, seeded by
// infra/db/031_add_fx_fuxion_catalog.sql) -- feeds the catalog pickers on
// product creation and the discount-percentage selector on purchases.
export const fxFuxionCatalogApi = {
  list: () => api.get('/fx/fuxion-catalog'),
}

// Frozen read-only history from the old single-product purchase flow --
// creating new purchases this way is gone, see fxPurchaseOrdersApi below.
export const fxPurchasesApi = {
  list: () => api.get('/fx/purchases'),
}

// Multi-product purchase orders (en_transito -> recibido/cancelado) --
// replaces fxPurchasesApi.create. See infra/db/032_add_fx_purchase_orders.sql.
export const fxPurchaseOrdersApi = {
  list: () => api.get('/fx/purchase-orders'),
  create: (data: any) => api.post('/fx/purchase-orders', data),
  update: (id: number, data: any) => api.put(`/fx/purchase-orders/${id}`, data),
  receive: (id: number, data?: { arrival_date?: string }) => api.post(`/fx/purchase-orders/${id}/receive`, data || {}),
  cancel: (id: number) => api.post(`/fx/purchase-orders/${id}/cancel`),
}

export const fxCustomersApi = {
  list: () => api.get('/fx/customers'),
  create: (data: any) => api.post('/fx/customers', data),
  update: (id: number, data: any) => api.put(`/fx/customers/${id}`, data),
  delete: (id: number) => api.delete(`/fx/customers/${id}`),
}

export const fxSalesApi = {
  list: () => api.get('/fx/sales'),
  create: (data: any) => api.post('/fx/sales', data),
  createBulk: (data: {
    customer_id: number
    items: any[]
    gps_latitude?: number | null
    gps_longitude?: number | null
  }) => api.post('/fx/sales/bulk', data),
}

export const fxExpensesApi = {
  list: (startDate?: string, endDate?: string) =>
    api.get('/fx/expenses', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  create: (data: any) => api.post('/fx/expenses', data),
  update: (id: number, data: any) => api.put(`/fx/expenses/${id}`, data),
}

export const fxOrdersApi = {
  list: () => api.get('/fx/orders'),
  create: (data: any) => api.post('/fx/orders', data),
  update: (id: number, data: any) => api.put(`/fx/orders/${id}`, data),
  confirm: (id: number) => api.post(`/fx/orders/${id}/confirm`),
  cancel: (id: number) => api.post(`/fx/orders/${id}/cancel`),
}

export const fxReportsApi = {
  getCashPosition: (startDate?: string, endDate?: string) =>
    api.get('/fx/reports/cash-position', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  getSalesTrend: (startDate?: string, endDate?: string) =>
    api.get('/fx/reports/sales-trend', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      },
    }),
  getTopProducts: (startDate?: string, endDate?: string, limit?: number) =>
    api.get('/fx/reports/top-products', {
      params: {
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(limit ? { limit } : {}),
      },
    }),
}

export const businessTypesApi = {
  list: () => api.get('/business-types'),
}

export const ordersApi = {
  list: () => api.get('/orders'),
  create: (data: {
    customer_id: number | null
    promised_date?: string | null
    notes?: string
    items: any[]
    gps_latitude?: number | null
    gps_longitude?: number | null
  }) => api.post('/orders', data),
  update: (id: number, data: { promised_date?: string | null; notes?: string }) => api.put(`/orders/${id}`, data),
  addItem: (orderId: number, data: any) => api.post(`/orders/${orderId}/items`, data),
  cancelItems: (itemIds: number[], cancellationReason: string) =>
    api.post('/order-items/cancel', { item_ids: itemIds, cancellation_reason: cancellationReason }),
  convertToSale: (items: any[]) => api.post('/orders/convert-to-sale', { items }),
}

export const warehousesApi = {
  list: () => api.get('/warehouses'),
  create: (data: any) => api.post('/warehouses', data),
  update: (id: number, data: any) => api.put(`/warehouses/${id}`, data),
}

export const suppliesApi = {
  list: () => api.get('/supplies'),
  create: (data: any) => api.post('/supplies', data),
  update: (id: number, data: any) => api.put(`/supplies/${id}`, data),
}

export const greenCoffeeLotsApi = {
  list: (params?: { status?: string; warehouse_id?: number }) =>
    api.get('/green-coffee-lots', { params }),
  create: (data: any) => api.post('/green-coffee-lots', data),
  update: (id: number, data: any) => api.put(`/green-coffee-lots/${id}`, data),
  receive: (id: number, data?: any) => api.post(`/green-coffee-lots/${id}/receive`, data || {}),
  cancel: (id: number) => api.delete(`/green-coffee-lots/${id}`),
}

export const selectionLotsApi = {
  list: (params?: { source_lot_id?: number; status?: string }) =>
    api.get('/selection-lots', { params }),
  // create() accepts TWO body shapes:
  // - Chain-sourced (splits a green coffee lot by quality): { source_lot_id, splits: [{ quality_grade, kg_selected, warehouse_id, status?, origin_warehouse_id?, transit_eta?, notes? }] }
  // - Direct purchase (buying an already-selected lot, no source_lot_id/splits): { quality_grade, kg_purchased, price_per_kg?, assembly_cost?, freight_cost?, storage_cost?, supplier_id?, warehouse_id, status?, origin_warehouse_id?, transit_eta?, notes? }
  // Response is always { ids: [...], lot_codes: [...] } (array of one for direct purchase).
  create: (data: any) => api.post('/selection-lots', data),
  update: (id: number, data: any) => api.put(`/selection-lots/${id}`, data),
  cancel: (id: number) => api.delete(`/selection-lots/${id}`),
}

export const roastingBatchesApi = {
  list: (params?: { status?: string; warehouse_id?: number; source_selection_lot_id?: number }) =>
    api.get('/roasting-batches', { params }),
  // create() accepts TWO body shapes:
  // - Chain-sourced (roasts a selection lot, created as status 'pending', kg_output filled later via /complete): { source_selection_lot_id, kg_input, warehouse_id, ... }
  // - Direct purchase (buying already-roasted coffee, no source_selection_lot_id, status must be 'at_rest'/'in_transit' — never 'pending'): { kg_purchased, price_per_kg?, assembly_cost?, freight_cost?, storage_cost?, supplier_id?, warehouse_id, status?, ... }
  create: (data: any) => api.post('/roasting-batches', data),
  update: (id: number, data: any) => api.put(`/roasting-batches/${id}`, data),
  complete: (id: number, data: { kg_output: number }) => api.post(`/roasting-batches/${id}/complete`, data),
  cancel: (id: number) => api.delete(`/roasting-batches/${id}`),
}

export const blendingBatchesApi = {
  list: () => api.get('/blending-batches'),
  // create() accepts TWO body shapes:
  // - Chain-sourced (blends a roasting batch with wheat/sugar/barley): { source_roasting_batch_id, kg_roasted_coffee_used, kg_wheat?, kg_sugar?, kg_barley?, ..., warehouse_id, ... }
  // - Direct purchase (buying an already-blended product, no source_roasting_batch_id; must NOT send kg_wheat/kg_sugar/kg_barley, status must be 'at_rest'/'in_transit' — never 'pending'): { kg_purchased, price_per_kg?, assembly_cost?, freight_cost?, storage_cost?, supplier_id?, warehouse_id, status?, ... }
  create: (data: any) => api.post('/blending-batches', data),
  update: (id: number, data: any) => api.put(`/blending-batches/${id}`, data),
  complete: (id: number, data?: any) => api.post(`/blending-batches/${id}/complete`, data || {}),
  cancel: (id: number) => api.delete(`/blending-batches/${id}`),
}

export const packagingBatchesApi = {
  list: () => api.get('/packaging-batches'),
  create: (data: any) => api.post('/packaging-batches', data),
  update: (id: number, data: any) => api.put(`/packaging-batches/${id}`, data),
  complete: (id: number, data?: any) => api.post(`/packaging-batches/${id}/complete`, data || {}),
  cancel: (id: number) => api.delete(`/packaging-batches/${id}`),
}

export const suppliersApi = {
  list: () => api.get('/suppliers'),
  create: (data: any) => api.post('/suppliers', data),
  update: (id: number, data: any) => api.put(`/suppliers/${id}`, data),
}

export const servicesApi = {
  list: () => api.get('/services'),
  create: (data: any) => api.post('/services', data),
  update: (id: number, data: any) => api.put(`/services/${id}`, data),
}

export const quotesApi = {
  list: () => api.get('/quotes'),
  create: (data: any) => api.post('/quotes', data),
  update: (id: number, data: any) => api.put(`/quotes/${id}`, data),
}

export const expensesApi = {
  list: (params?: { category?: string; start_date?: string; end_date?: string; supplier_id?: number }) =>
    api.get('/expenses', { params }),
  create: (data: any) => api.post('/expenses', data),
  update: (id: number, data: any) => api.put(`/expenses/${id}`, data),
}

export const exportApi = {
  emailFile: (data: {
    recipient_email: string
    subject: string
    filename: string
    content_base64: string
    mime_type: 'text/csv' | 'application/pdf'
  }) => api.post('/export/email', data),
}

export const messagesApi = {
  threads: () => api.get('/messages/threads'),
  list: (sellerId: number) => api.get(`/messages/${sellerId}`),
  send: (sellerId: number, body: string) => api.post(`/messages/${sellerId}`, { body }),
  unreadCount: () => api.get('/messages/unread-count'),
}
